import { AdminLayout } from '@/components/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Link, router } from '@inertiajs/react';
import { createColumnHelper, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, Clock, Eye, FileClock, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import Application from './Application';
import EditUserDialog from './EditUserDialog';
interface User {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  role: 'mentor' | 'mentee';
  category: 'pre-health' | 'graduate' | 'fellowship';
  email_verified: boolean;
  admin_verified: boolean;
  admin_rejected: boolean;
  created_at: string;
  unverified_with_completed_application: boolean;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface Filters {
  status: string;
  role: string;
  category: string;
  search: string;
  match_status: string;
}

interface Counts {
  awaiting_review: number;
  incomplete_application: number;
  verified: number;
  rejected: number;
  all: number;
}

interface Props {
  users: User[];
  pagination: Pagination;
  filters: Filters;
  counts: Counts;
}

const columnHelper = createColumnHelper<User>();

function AdminUsersIndex({ users, pagination, filters, counts }: Props) {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null)
  const getStatusBadge = (user: User) => {
    if (user.admin_rejected) {
      return (
        <Badge variant={'destructive'} className='flex items-center gap-1 w-fit'>
          <XCircle className='h-3 w-3' />
          Rejected
        </Badge>
      )
    }
    if (user.admin_verified && user.email_verified) {
      return (
        <Badge variant={'default'} className='flex items-center gap-1 w-fit'>
          <CheckCircle2 className='h-3 w-3' />
          Verified
        </Badge>
      )
    }
    if (user.unverified_with_completed_application) {
      return (
        <Badge variant={'secondary'} className='flex items-center gap-1 w-fit'>
          <Clock className='w-3 h-3' />
          Awaiting review
        </Badge>
      )
    }
    return (
      <Badge variant={'outline'} className='flex items-center gap-1 w-fit text-gray-600'>
        <FileClock className='w-3 h-3' />
        Incomplete application
      </Badge>
    )
  }

  const columns: ColumnDef<User, any>[] = [
    columnHelper.accessor(
      (row) => `${row.first_name} ${row.last_name}`,
      {
        id: 'name',
        header: 'Name',
        cell: (info) => (
          <div className='font-medium'>{info.getValue()}</div>
        )
      }
    ),
    columnHelper.accessor('email_address', {
      header: 'Email',
      cell: (info) => (
        <div className='text-sm text-gray-600'>{info.getValue()}</div>
      )
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: (info) => (
        <span className='capitalize text-sm'>{info.getValue()}</span>
      )
    }),
    columnHelper.accessor('category', {
      header: 'Category',
      cell: (info) => {
        const category = info.getValue()
        return (
          <span className='text-sm'>
            {category === 'pre-health' ? 'Pre-health' : category === 'graduate' ? 'Graduate' : 'Fellowship'}
          </span>
        )
      }
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => getStatusBadge(row.original)
    }),
    columnHelper.accessor('created_at', {
      header: 'Joined',
      cell: (info) => (
        <div className='text-sm text-gray-600'>
          {formatDistanceToNow(new Date(info.getValue()), { addSuffix: true })}
        </div>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        if (row.original.unverified_with_completed_application) {
          return (
            <div className='flex gap-2'>
              <Button variant={'link'} className='underline' onClick={() => setSelectedUserId(row.original.id)}>
                View application
              </Button>
              <Button variant={'link'} size={'sm'} className='underline' onClick={() => setSelectedUserForEdit(row.original)}>
                Edit
              </Button>
            </div>
          )
        } else {
          return (<div className="flex gap-2">
            <Link href={`/admin/users/${row.original.id}`}>
              <Button variant="link" size="sm" className='underline'>
                View Profile
              </Button>
            </Link>
            <Button variant={'link'} size={'sm'} className='underline' onClick={() => setSelectedUserForEdit(row.original)}>
              Edit
            </Button>
          </div>
          )
        }
      },
    }),
  ]

  const table = useReactTable({
    data: users,
    columns,
    pageCount: pagination.total_pages,
    state: {
      pagination: {
        pageIndex: pagination.page - 1,
        pageSize: pagination.per_page
      }
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel()
  })
  const handleFilterChange = (filterType: 'status' | 'role' | 'category' | 'match_status', value: string) => {
    const newFilters = {
      ...filters,
      [filterType]: value,
      page: 1
    }

    if (filterType === 'match_status' && (value === 'matched' || value === 'unmatched')) {
      newFilters.status = 'verified'
    }
    router.get('/admin/users', newFilters, {
      preserveScroll: true,
      only: ['users', 'pagination', 'filters', 'counts']
    })
  }

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;

    router.get('/admin/users', {
      ...filters,
      search,
      page: 1
    }, {
      preserveScroll: true,
      preserveState: true
    })
  }
  return (
    <AdminLayout title='User Management'>
      <div className='flex flex-col gap-6'>
        <Card>
          <CardContent className='pt-6 space-y-6'>
            <form onSubmit={handleSearch} className='flex gap-4'>
              <div className='flex-1'>
                <Input
                  type='text'
                  name='search'
                  placeholder='Search by name or email...'
                  defaultValue={filters.search}
                />
              </div>
              <Button type='submit'>Search</Button>
              {filters.search && (
                <Button
                  type='button'
                  variant={'outline'}
                  onClick={() => {
                    router.get('/admin/users', {
                      ...filters,
                      search: ''
                    })
                  }}
                >Clear</Button>
              )}
            </form>
            <div className='flex flex-wrap gap-4 items-center'>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Status:</label>
                <Select value={filters.status}
                  onValueChange={(value) => handleFilterChange('status', value)}
                  disabled={filters.match_status === 'matched' || filters.match_status === 'unmatched'}
                >
                  <SelectTrigger className='w-[180px]'>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='awaiting_review'>Awaiting review</SelectItem>
                    <SelectItem value='incomplete_application'>Incomplete application</SelectItem>
                    <SelectItem value='verified'>Verified</SelectItem>
                    <SelectItem value='rejected'>Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Role:</label>
                <Select value={filters.role} onValueChange={(value) => handleFilterChange('role', value)}>
                  <SelectTrigger className='w-[150px]'>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='mentor'>Mentors</SelectItem>
                    <SelectItem value='mentee'>Mentees</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Category:</label>
                <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
                  <SelectTrigger className='w-[150px]'>
                    <SelectValue className='Select category' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='pre-health'>Pre-health</SelectItem>
                    <SelectItem value='graduate'>Graduate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Match Status:</label>
                <Select
                  value={filters.match_status}
                  onValueChange={(value) => handleFilterChange('match_status', value)}
                >
                  <SelectTrigger className='w-[200px]'>
                    <SelectValue placeholder="Select match status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="unmatched">Unmatched</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='text-sm text-muted-foreground'>
              Showing {users.length} of {pagination.total} users
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='pl-4'>
            {users.length === 0 ? (
              <div className='text-center py-16'>
                <div className='inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4'>
                  <XCircle className='h-8 w-8 text-gray-400' />
                </div>
                <p className='text-gray-900 font-medium mb-1'>No users found</p>
                <p className='text-sm text-muted-foreground'>
                  {filters.search ? 'Try adjusting your search or filters' : 'No users match the selected filters'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {table.getFlatHeaders().map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : (
                          <div>
                            {typeof header.column.columnDef.header === 'function' ? header.column.columnDef.header(header.getContext()) : header.column.columnDef.header}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {typeof cell.column.columnDef.cell === 'function' ? cell.column.columnDef.cell(cell.getContext()) : cell.getValue()}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        {pagination.total_pages > 1 && (
          <div className='pt-6'>
            <div className='flex items-center justify-between'>
              <div className='text-sm text-muted-foreground'>
                Page {pagination.page} of {pagination.total_pages}
              </div>

              <div className='flex items-center gap-2'>
                <Button
                  variant={'outline'}
                  size={'sm'}
                  disabled={pagination.page === 1}
                  onClick={() => {
                    router.get('/admin/users', {
                      ...filters,
                      page: pagination.total - 1
                    }, {
                      preserveScroll: true,
                      preserveState: true
                    })
                  }}
                >
                  Previous
                </Button>

                <div className='flex gap-1'>
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                    .filter((pageNum) => {
                      return (
                        pageNum === 1 || pageNum === pagination.total_pages || Math.abs(pageNum - pagination.page) <= 2
                      )
                    })
                    .map((pageNum, index, array) => {
                      const prevPageNum = array[index - 1];
                      const showEllipsis = prevPageNum && pageNum - prevPageNum > 1;

                      return (
                        <div key={pageNum} className='flex items-center gap-1'>
                          {showEllipsis && (
                            <span className='px-2 text-muted-foreground'>...</span>
                          )}
                          <Button
                            variant={pagination.page === pageNum ? 'default' : 'outline'}
                            size={'sm'}
                            className='min-w-[40px]'
                            onClick={() => {
                              router.get('/admin/users', {
                                ...filters,
                                page: pageNum
                              }, {
                                preserveScroll: true,
                                preserveState: true
                              })
                            }}
                          >
                            {pageNum}
                          </Button>
                        </div>
                      )
                    })
                  }
                </div>
                <Button
                  variant={'outline'}
                  size={'sm'}
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => {
                    router.get('/admin/users', {
                      ...filters,
                      page: pagination.page + 1
                    }, {
                      preserveScroll: true,
                      preserveState: true
                    })
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {selectedUserId && (
        <Application
          userId={selectedUserId}
          open={!!selectedUserId}
          onOpenChange={(open) => !open && setSelectedUserId(null)}
        />
      )}
      {selectedUserForEdit && (
        <EditUserDialog
          user={selectedUserForEdit}
          open={!!selectedUserForEdit}
          onOpenChange={(open) => !open && setSelectedUserForEdit(null)}
        />
      )}
    </AdminLayout>
  )
}


export default AdminUsersIndex;
